<template>
  <div class="flow-details flow-details-path">
    <div class="flow-details__title">
      <div class="subtitle">
        {{ t('Rule settings') }}
      </div>

      <div class="flow-details__header-action-wr">
        <a class="flow-details__duplicate" @click="emit('duplicate', rule)">
          <ui-icon size="12" class="mr-4" :color="$colors.blue" icon="fas fa-copy"></ui-icon>{{ t('Duplicate rule') }}
        </a>
        <a class="flow-details__remove" @click="emit('delete', rule)">
          <ui-icon size="12" class="mr-4" :color="$colors.red" icon="far fa-trash-alt"></ui-icon>{{ t('Delete rule') }}
        </a>
        <div class="flow-details__action-wr ml-4">
          <ui-switcher class="ml-8" v-model="ruleActive">
            <template #label>
              <span v-if="ruleActive">{{ t('____x.active') }}</span>
              <span v-if="!ruleActive">{{ t('Inactive') }}</span>
            </template>
          </ui-switcher>
        </div>
      </div>
    </div>

    <div class="flow-details__wrap">
      <div class="flow-details__inp-wr">
        <div>
          <div class="flow-details__inp-name">
            <ui-input
              v-model="rule.name"
              :placeholder="placeholder || '   '"
              :label="t('Name')"
              name="name"
              :hide-details="1"
            ></ui-input>
          </div>
        </div>
      </div>
      <div>
        <ui-radio-group class="flow-radio-gr mt-8 mb-8" v-model="rule.logicalRelation">
          <template #label>
            <label class="font-weight-bold">{{ t('Logical relation') }}
              <ui-tooltip-icon
                preset="help"
                class="mb-2 ml-1"
                :text="t('Logical relations between conditions in rule')"
              ></ui-tooltip-icon>
            </label>
          </template>
          <div class="d-flex align-baseline mt-8">
            <ui-radio value="and" :label="t('Logical AND')"></ui-radio>
            <ui-radio class="ml-16" value="or" :label="t('Logical OR')"></ui-radio>
          </div>
        </ui-radio-group>
      </div>
      <div class="action-buttons button-wrapper mb-8">
        <div class="action-buttons--left">
          <ui-button
            icon="fas fa-plus"
            :text="t('Add condition')"
            button-style="accent"
            :menu-list="menuList"
            :menu-props="{
              maxHeight: 'calc(100vh - 150px)',
              nudgeTop: 150,
              condenced: true
            }"
          />

          <ui-button
            icon="far fa-trash-alt"
            :text="t('Remove all conditions')"
            @click="deselectAll"
          />
        </div>
        <ui-info
          v-if="isClientHintsNoticeVisible"
          :close="true"
          name="clientHintsNoticeRulesFlow"
          width="w-auto"
        >
          <div>
            <a href="/app/profile">
              {{ t('Activate Client Hints to get more details on OS, device, browser reports.') }}
            </a>
          </div>
        </ui-info>
      </div>

      <div class="flow-rule__card">
        <div v-if="selectedDictionaryEmpty" class="flow-rule__empty">{{ t('Rule conditions are empty') }}</div>
        <div v-else :key="ruleIndex">
          <brand-and-model
            v-if="selectedDictionary['brand']"
            @close="remove('brand')"
            :conditions="rule.conditions"
          ></brand-and-model>
          <browser-and-version
            v-if="selectedDictionary['browser']"
            @close="remove('browser')"
            :conditions="rule.conditions"
          ></browser-and-version>
          <connection-type
            v-if="selectedDictionary['connectType']"
            @close="remove('connectType')"
            :conditions="rule.conditions"
          ></connection-type>
          <dictionary
            v-if="selectedDictionary['country']"
            @close="remove('country')"
            :title="t('Country')"
            :searchPlaceholder="t('Select countries...')"
            api="countries"
            name="country"
            :allow-custom="false"
            :conditions="rule.conditions"
            :withCustomArea="true"
          ></dictionary>
          <dictionary
            v-if="selectedDictionary['state']"
            @close="remove('state')"
            :title="t('State / region')"
            :searchPlaceholder="t('Type state / region name...')"
            api="states"
            name="state"
            :allow-custom="false"
            :conditions="rule.conditions"
            :withCustomArea="true"
          ></dictionary>
          <dictionary
            v-if="selectedDictionary['city']"
            @close="remove('city')"
            :title="t('City')"
            :searchPlaceholder="t('Type city name...')"
            name="city"
            api="cities"
            :allow-custom="false"
            :conditions="rule.conditions"
            :withCustomArea="true"
          ></dictionary>
          <custom-token
            v-if="selectedDictionary['customToken']"
            @close="remove('customToken')"
            :conditions="rule.conditions"
          ></custom-token>
          <query-header
            v-if="selectedDictionary['queryHeader']"
            @close="remove('queryHeader')"
            :conditions="rule.conditions"
          ></query-header>
          <day-of-week
            v-if="selectedDictionary['dayOfWeek']"
            @close="remove('dayOfWeek')"
            :conditions="rule.conditions"
          ></day-of-week>
          <time-of-day
            v-if="selectedDictionary['timeOfDay']"
            @close="remove('timeOfDay')"
            :conditions="rule.conditions"
          ></time-of-day>
          <timezone
            v-if="isTimezoneAvailable"
            :conditions="rule.conditions"
          ></timezone>
          <device-type
            v-if="selectedDictionary['deviceType']"
            @close="remove('deviceType')"
            :conditions="rule.conditions"
          ></device-type>
          <ip
            v-if="selectedDictionary['ip']"
            @close="remove('ip')"
            :conditions="rule.conditions"
            :withCustomArea="true"
          ></ip>
          <dictionary
            v-if="selectedDictionary['isp']"
            @close="remove('isp')"
            :title="t('ISP')"
            :searchPlaceholder="t('Type ISP name...')"
            name="isp"
            api="isps"
            :allow-custom="false"
            :conditions="rule.conditions"
            :withCustomArea="true"
          ></dictionary>
          <dictionary
            v-if="selectedDictionary['mobileCarrier']"
            @close="remove('mobileCarrier')"
            :title="t('Mobile Carrier')"
            :searchPlaceholder="t('Type mobile carrier name...')"
            name="mobileCarrier"
            api="mobile-carriers"
            :conditions="rule.conditions"
            :withCustomArea="true"
          ></dictionary>
          <dictionary
            v-if="selectedDictionary['language']"
            @close="remove('language')"
            :title="t('Language')"
            :searchPlaceholder="t('Select languages...')"
            api="languages"
            name="language"
            :conditions="rule.conditions"
            :allow-custom="false"
            :withCustomArea="true"
          ></dictionary>
          <os-and-version
            v-if="selectedDictionary['os']"
            @close="remove('os')"
            :conditions="rule.conditions"
          ></os-and-version>
          <referrerDomain
            v-if="selectedDictionary['referrerDomain']"
            @close="remove('referrerDomain')"
            :conditions="rule.conditions"
          ></referrerDomain>
          <referrer
            v-if="selectedDictionary['referrer']"
            @close="remove('referrer')"
            :conditions="rule.conditions"
          ></referrer>
          <user-agent
            v-if="selectedDictionary['userAgent']"
            @close="remove('userAgent')"
            :conditions="rule.conditions"
            :withCustomArea="true"
          ></user-agent>
          <uniqueness
            v-if="selectedDictionary['uniqueness']"
            @close="remove('uniqueness')"
            :conditions="rule.conditions"
          ></uniqueness>
          <uniq-offer-viewer
            v-if="selectedDictionary['uniqOfferViewer']"
            @close="remove('uniqOfferViewer')"
            :conditions="rule.conditions"
          ></uniq-offer-viewer>
          <bot-filter
            v-if="selectedDictionary['botMark']"
            @close="remove('botMark')"
            :conditions="rule.conditions"
          ></bot-filter>
          <custom
            v-for="index in customConditions"
            :key="index"
            :title="t('Custom {0}', [index])"
            :name="'custom' + index"
            :conditions="rule.conditions"
            @close="remove('custom' + index)"
          ></custom>
          <webview
            v-if="selectedDictionary['webViewMark']"
            @close="remove('webViewMark')"
            :conditions="rule.conditions"
          ></webview>
          <crawler
            v-if="selectedDictionary['crawler']"
            @close="remove('crawler')"
            :conditions="rule.conditions"
          ></crawler>
          <proxy
            v-if="selectedDictionary['proxy']"
            @close="remove('proxy')"
            :conditions="rule.conditions"
          ></proxy>
          <ipBlacklisted
            v-if="selectedDictionary['ipBlacklisted']"
            @close="remove('ipBlacklisted')"
            :conditions="rule.conditions"
          ></ipBlacklisted>
          <ipDataCenter
            v-if="selectedDictionary['ipDataCenter']"
            @close="remove('ipDataCenter')"
            :conditions="rule.conditions"
          ></ipDataCenter>
          <visit-cap
            v-if="selectedDictionary['visitCap']"
            @close="remove('visitCap')"
            :conditions="rule.conditions"
          ></visit-cap>
          <ipWithFrequentEvents
            v-if="selectedDictionary['ipWithFrequentEvents']"
            @close="remove('ipWithFrequentEvents')"
            :conditions="rule.conditions"
          ></ipWithFrequentEvents>
          <ipWithFrequentCampaignEvents
            v-if="selectedDictionary['ipWithFrequentCampaignEvents']"
            @close="remove('ipWithFrequentCampaignEvents')"
            :conditions="rule.conditions"
          ></ipWithFrequentCampaignEvents>
          <external-id
            v-if="selectedDictionary['externalId']"
            @close="remove('externalId')"
            :conditions="rule.conditions"
          ></external-id>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import BrandAndModel from './conditions/brand-and-model.vue';
import BrowserAndVersion from './conditions/browser-and-version.vue';
import OsAndVersion from './conditions/os-and-version.vue';
import Dictionary from './conditions/dictionary.vue';
import ConnectionType from './conditions/connection-type.vue';
import Custom from './conditions/custom.vue';
import CustomToken from './conditions/custom-token.vue';
import QueryHeader from './conditions/query-header.vue';
import DayOfWeek from './conditions/day-of-week.vue';
import DeviceType from './conditions/device-type.vue';
import Ip from './conditions/ip.vue';
import Referrer from './conditions/referrer.vue';
import ReferrerDomain from './conditions/referrer-domain.vue';
import UserAgent from './conditions/user-agent.vue';
import TimeOfDay from './conditions/time-of-day.vue';
import Timezone from './conditions/timezone.vue';
import Webview from './conditions/webview.vue';
import Uniqueness from './conditions/uniqueness.vue';
import UniqOfferViewer from './conditions/uniq-offer-viewer.vue';
import BotFilter from './conditions/bot-filter.vue';
import Crawler from './conditions/crawler.vue';
import Proxy from './conditions/proxy.vue';
import VisitCap from './conditions/visit-cap.vue';
import IpBlacklisted from './conditions/ip-blacklisted.vue';
import IpDataCenter from './conditions/ip-data-center.vue';
import IpWithFrequentEvents from './conditions/ip-with-frequent-events.vue';
import IpWithFrequentCampaignEvents from './conditions/ip-with-frequent-campaign-events.vue';
import ExternalId from './conditions/external-id.vue';
import { useFlowRuleSettings } from './useFlowRuleSettings';

const $colors = useColors();

const props = defineProps<{
  rule: {
    conditions: Record<string, unknown>;
    logicalRelation?: string;
    status?: string;
    name?: string;
  };
  ruleIndex: number;
  placeholder?: string;
  errorsDict?: Record<string, string[]>;
}>();

const emit = defineEmits<{
  duplicate: [rule: typeof props.rule];
  delete: [rule: typeof props.rule];
}>();

const {
  t,
  menuList,
  selectedDictionary,
  selectedDictionaryEmpty,
  customConditions,
  isTimezoneAvailable,
  isClientHintsNoticeVisible,
  ruleActive,
  remove,
  deselectAll,
} = useFlowRuleSettings(props);
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.button-wrapper {
  display: flex;
}

.action-buttons{
  display: flex;
  justify-content:space-between;
  flex-wrap: wrap;
  gap:6px;
}

.action-buttons--left{
  display: flex;
  flex-wrap: nowrap;
}
</style>
